/**
 * Eurostat API client — typed fetchers for government finance data.
 * Uses per-category queries (single value per request) for simplicity.
 */

const EUROSTAT_BASE = 'https://ec.europa.eu/eurostat/api/dissemination/statistics/1.0/data'

async function fetchSingle(dataset: string, params: Record<string, string>): Promise<number | null> {
  const url = new URL(`${EUROSTAT_BASE}/${dataset}`)
  url.searchParams.set('geo', 'FR')
  url.searchParams.set('format', 'JSON')
  url.searchParams.set('lang', 'fr')
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v)
  }
  const res = await fetch(url)
  if (!res.ok) return null
  const data = (await res.json()) as { value: Record<string, number | null> }
  return Object.values(data.value)[0] ?? null
}

// ── Recettes fiscales par type ──

export interface TaxItem {
  code: string
  label: string
  shortLabel: string
  value: number // M€
  pctOfTotal: number // % du total prélèvements
}

export const TAX_CATEGORIES: Array<{ code: string; label: string; shortLabel: string }> = [
  { code: 'D211', label: 'Taxes du type TVA', shortLabel: 'TVA' },
  { code: 'D212', label: "Droits d'importation", shortLabel: 'Importations' },
  { code: 'D214', label: 'Impôts sur produits (hors TVA)', shortLabel: 'Autres impôts produits' },
  { code: 'D29', label: 'Autres impôts sur la production', shortLabel: 'Autres impôts production' },
  { code: 'D51A', label: 'IR (personnes physiques)', shortLabel: 'IR' },
  { code: 'D51B', label: 'IS (sociétés)', shortLabel: 'IS' },
  { code: 'D59', label: 'Autres impôts courants', shortLabel: 'Autres impôts courants' },
  { code: 'D611', label: 'Cotisations sociales employeurs', shortLabel: 'Cotis. employeurs' },
  { code: 'D612', label: 'Cotisations sociales salariés', shortLabel: 'Cotis. salariés' },
  { code: 'D613', label: 'Cotisations sociales non-salariés', shortLabel: 'Cotis. non-salariés' },
]

/** Fetch all tax categories for a given year. Returns null if any request fails. */
export async function fetchTaxRevenues(year: string): Promise<TaxItem[]> {
  const items = await Promise.allSettled(
    TAX_CATEGORIES.map(async (cat) => {
      const val = await fetchSingle('gov_10a_taxag', {
        na_item: cat.code,
        unit: 'MIO_NAC',
        sector: 'S13',
        time: year,
      })
      return { ...cat, value: val ?? 0 }
    }),
  )

  const results = items
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((r): r is TaxItem => r !== null && r.value > 0)

  const total = results.reduce((s, r) => s + r.value, 0)
  return results.map((r) => ({ ...r, pctOfTotal: total > 0 ? (r.value / total) * 100 : 0 }))
}

// ── Dépenses COFOG ──

export interface CofogItem {
  code: string
  label: string
  value: number // M€
  pctOfTotal: number
}

const COFOG_CATEGORIES = [
  { code: 'GF01', label: 'Services généraux' },
  { code: 'GF02', label: 'Défense' },
  { code: 'GF03', label: 'Ordre public' },
  { code: 'GF04', label: 'Affaires économiques' },
  { code: 'GF05', label: 'Protection environnement' },
  { code: 'GF06', label: 'Logement' },
  { code: 'GF07', label: 'Santé' },
  { code: 'GF08', label: 'Culture & Loisirs' },
  { code: 'GF09', label: 'Enseignement' },
  { code: 'GF10', label: 'Protection sociale' },
]

export async function fetchCofogExpenditures(year: string): Promise<CofogItem[]> {
  const items = await Promise.allSettled(
    COFOG_CATEGORIES.map(async (cat) => {
      // P3 = dépenses de consommation finale des APU
      const val = await fetchSingle('gov_10a_exp', {
        cofog99: cat.code,
        unit: 'MIO_EUR',
        sector: 'S13',
        na_item: 'P3',
        time: year,
      })
      return { ...cat, value: val ?? 0 }
    }),
  )

  const results = items
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((r): r is CofogItem => r !== null && r.value > 0)

  const total = results.reduce((s, r) => s + r.value, 0)
  return results.map((r) => ({ ...r, pctOfTotal: total > 0 ? (r.value / total) * 100 : 0 }))
}

// ── Agrégats Maastricht ──

export interface MaastrichtData {
  deficitPctGdp: number | null
  debtPctGdp: number | null
  debtAbsolute: number | null // M€
  totalExpenditure: number | null // M€
  totalRevenue: number | null // M€
}

export async function fetchMaastricht(year: string): Promise<MaastrichtData> {
  const [deficitPctGdp, debtPctGdp, debtAbsolute, totalExpenditure, totalRevenue] = await Promise.all([
    fetchSingle('gov_10dd_edpt1', { na_item: 'B9', unit: 'PC_GDP', sector: 'S13', time: year }),
    fetchSingle('gov_10dd_edpt1', { na_item: 'GD', unit: 'PC_GDP', sector: 'S13', time: year }),
    fetchSingle('gov_10dd_edpt1', { na_item: 'GD', unit: 'MIO_EUR', sector: 'S13', time: year }),
    fetchSingle('gov_10a_main', { na_item: 'TE', unit: 'MIO_EUR', sector: 'S13', time: year }),
    fetchSingle('gov_10a_main', { na_item: 'TR', unit: 'MIO_EUR', sector: 'S13', time: year }),
  ])
  return { deficitPctGdp, debtPctGdp, debtAbsolute, totalExpenditure, totalRevenue }
}

// ── Séries temporelles ──

export async function fetchDeficitSeries(fromYear: number, toYear: number): Promise<Array<{ year: number; value: number | null }>> {
  const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => String(fromYear + i))
  const results = await Promise.allSettled(
    years.map(async (y) => {
      const val = await fetchSingle('gov_10dd_edpt1', { na_item: 'B9', unit: 'PC_GDP', sector: 'S13', time: y })
      return { year: Number(y), value: val }
    }),
  )
  return results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((r): r is { year: number; value: number | null } => r !== null)
}

/** Time series for a specific tax code (M€) */
export async function fetchTaxSeries(
  code: string,
  fromYear: number,
  toYear: number,
): Promise<Array<{ year: number; value: number | null }>> {
  const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => String(fromYear + i))
  const results = await Promise.allSettled(
    years.map(async (y) => {
      const val = await fetchSingle('gov_10a_taxag', {
        na_item: code, unit: 'MIO_NAC', sector: 'S13', time: y,
      })
      return { year: Number(y), value: val }
    }),
  )
  return results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((r): r is { year: number; value: number | null } => r !== null)
}

/** Time series for a specific COFOG code (M€) */
export async function fetchCofogSeries(
  code: string,
  fromYear: number,
  toYear: number,
): Promise<Array<{ year: number; value: number | null }>> {
  const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => String(fromYear + i))
  const results = await Promise.allSettled(
    years.map(async (y) => {
      const val = await fetchSingle('gov_10a_exp', {
        cofog99: code, unit: 'MIO_EUR', sector: 'S13', na_item: 'P3', time: y,
      })
      return { year: Number(y), value: val }
    }),
  )
  return results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((r): r is { year: number; value: number | null } => r !== null)
}

// ── Comparaisons UE ──

const EU_COUNTRIES: Array<{ code: string; label: string }> = [
  { code: 'DE', label: 'Allemagne' },
  { code: 'FR', label: 'France' },
  { code: 'IT', label: 'Italie' },
  { code: 'ES', label: 'Espagne' },
  { code: 'NL', label: 'Pays-Bas' },
  { code: 'BE', label: 'Belgique' },
  { code: 'AT', label: 'Autriche' },
  { code: 'PL', label: 'Pologne' },
  { code: 'PT', label: 'Portugal' },
  { code: 'SE', label: 'Suède' },
  { code: 'DK', label: 'Danemark' },
  { code: 'FI', label: 'Finlande' },
]

async function fetchSingleForCountry(dataset: string, geo: string, params: Record<string, string>): Promise<number | null> {
  const url = new URL(`${EUROSTAT_BASE}/${dataset}`)
  url.searchParams.set('geo', geo)
  url.searchParams.set('format', 'JSON')
  url.searchParams.set('lang', 'fr')
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v)
  }
  const res = await fetch(url)
  if (!res.ok) return null
  const data = (await res.json()) as { value: Record<string, number | null> }
  return Object.values(data.value)[0] ?? null
}

export interface CountryComparison {
  code: string
  label: string
  deficitPctGdp: number | null
  debtPctGdp: number | null
  totalTaxesPctGdp: number | null
}

export async function fetchEuComparison(year: string): Promise<CountryComparison[]> {
  const results = await Promise.allSettled(
    EU_COUNTRIES.map(async (country) => {
      const [deficitPctGdp, debtPctGdp, totalTaxesPctGdp] = await Promise.all([
        fetchSingleForCountry('gov_10dd_edpt1', country.code, { na_item: 'B9', unit: 'PC_GDP', sector: 'S13', time: year }),
        fetchSingleForCountry('gov_10dd_edpt1', country.code, { na_item: 'GD', unit: 'PC_GDP', sector: 'S13', time: year }),
        fetchSingleForCountry('gov_10a_taxag', country.code, { na_item: 'D2_D5_D91', unit: 'PC_GDP', sector: 'S13', time: year }),
      ])
      return { ...country, deficitPctGdp, debtPctGdp, totalTaxesPctGdp }
    }),
  )
  return results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((r): r is CountryComparison => r !== null)
}
